import React from "react";
import {
  Box,
  Chip,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from "@mui/material";
import {
  clampDuration,
  formatDurationLabel,
  getClipLimitForModel,
  maxDurationForModel,
  minDurationForModel,
  snapClipDuration,
} from "../durationOptions";
import {
  needsAutoStitch,
  distributeSegmentDurations,
} from "../klingSegmentPlan";
import { reconcileScenes } from "../scenePlan";

/** Quick totals beyond one clip, always including up to 5 minutes. */
function buildTotalPresets(clipMax) {
  const max = maxDurationForModel();
  const out = [];
  const seen = new Set();
  const push = (sec, { force = false } = {}) => {
    const n = Math.round(Number(sec) || 0);
    if (n < 1 || n > max || seen.has(n)) return;
    // Skip lengths that already live in the one-clip row — except forced shared
    // presets like 15s (shown before 20s for every model).
    if (!force && clipMax != null && n <= clipMax) return;
    seen.add(n);
    out.push(n);
  };

  // Shared total-length ladder (not model-specific).
  for (const sec of [15, 20, 30, 45, 60, 90, 120, 180, 240, 300]) {
    push(sec, { force: true });
  }
  // Also include doubles/triples of the model clip cap when useful.
  if (clipMax != null) {
    for (const mult of [2, 3, 4, 6]) push(clipMax * mult);
  }
  return out.sort((a, b) => a - b);
}

/**
 * Snap only when the target still fits in a single API clip.
 * Longer totals (up to 5m) are left intact so auto-stitch can split them.
 */
function snapTotalDuration(sec, modelId, clipOpts) {
  const limit = getClipLimitForModel(modelId, clipOpts);
  const n = clampDuration(sec);
  if (needsAutoStitch(modelId, n, clipOpts)) return n;
  if (limit.allowed?.length || limit.maxSec != null) {
    return snapClipDuration(n, limit);
  }
  return n;
}

/**
 * Model-aware duration picker.
 * - One-clip chips / short slider for native model lengths (6s, 10s, 15s, …)
 * - Total length up to 5 minutes via presets + slider (auto-split into scenes)
 */
export default function DurationSelect({ form, onChange, lang, modelId }) {
  const mid = modelId || form.model;
  const clipOpts = {
    hailuoResolution: form.hailuoResolution,
    resolution: form.resolution,
  };
  const limit = getClipLimitForModel(mid, clipOpts);
  const min = Math.max(minDurationForModel(), limit.minSec || minDurationForModel());
  const totalMax = maxDurationForModel();
  const clipMax = limit.maxSec;
  const stitchable = Boolean(limit.stitchable && clipMax != null);
  const discrete = Array.isArray(limit.allowed) && limit.allowed.length > 0 ? limit.allowed : null;

  const rawValue = clampDuration(form.duration);
  const value = snapTotalDuration(rawValue, mid, clipOpts);
  const willStitch = needsAutoStitch(mid, value, clipOpts);
  const segments = willStitch ? distributeSegmentDurations(value, mid, clipOpts) : [value];
  const sceneCount =
    Array.isArray(form.scenes) && form.scenes.length > 1
      ? form.scenes.length
      : segments.length;
  const totalPresets = stitchable ? buildTotalPresets(clipMax) : [];

  // Keep form.duration snapped for discrete single-clip values (e.g. Grok 7 → 6).
  React.useEffect(() => {
    if (Number(form.duration) === value) return;
    const next = { ...form, duration: value };
    onChange({ ...next, scenes: reconcileScenes(next) });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snap only when value drifts
  }, [value, mid]);

  const applyDuration = (sec) => {
    const nextDur = snapTotalDuration(sec, mid, clipOpts);
    const next = { ...form, duration: nextDur };
    onChange({ ...next, scenes: reconcileScenes(next) });
  };

  const oneClipOptions = discrete
    ? discrete
    : clipMax != null
      ? Array.from(
          new Set(
            [min, Math.round((min + clipMax) / 2), clipMax].map((s) =>
              snapClipDuration(s, limit),
            ),
          ),
        ).sort((a, b) => a - b)
      : [];

  const isOneClipSelected = !willStitch && oneClipOptions.includes(value);
  const longerMin = clipMax != null ? Math.min(totalMax, clipMax + 1) : min;

  const totalSliderMarks = [
    { value: min, label: formatDurationLabel(min) },
    ...(clipMax != null && clipMax > min && clipMax < totalMax
      ? [{ value: clipMax, label: formatDurationLabel(clipMax) }]
      : []),
    { value: 60, label: "1m" },
    { value: 180, label: "3m" },
    { value: totalMax, label: "5m" },
  ].filter((m, i, arr) => arr.findIndex((x) => x.value === m.value) === i);

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ minHeight: 22, mb: 1 }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {lang?.videoStudioDuration || "Duration"}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {willStitch || sceneCount > 1 ? (
            <Chip
              size="small"
              label={`${sceneCount} ${lang?.videoStudioScenesShort || "scenes"} · ${formatDurationLabel(value)}`}
              sx={{ height: 22, fontWeight: 700, fontSize: "0.7rem" }}
            />
          ) : (
            <Typography variant="body2" fontWeight={800} color="primary.main" sx={{ lineHeight: 1 }}>
              {formatDurationLabel(value)}
            </Typography>
          )}
        </Stack>
      </Stack>

      {oneClipOptions.length > 0 ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.75, lineHeight: 1.3 }}
          >
            {lang?.videoStudioDurationOneClip || "One clip"}
            {clipMax != null
              ? ` · ${lang?.videoStudioDurationUpTo || "up to"} ${formatDurationLabel(clipMax)} ${lang?.videoStudioDurationPerGeneration || "per generation"}`
              : ""}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={isOneClipSelected ? value : null}
            onChange={(_, v) => {
              if (v == null) return;
              applyDuration(v);
            }}
            sx={{
              flexWrap: "wrap",
              gap: 0.75,
              "& .MuiToggleButtonGroup-grouped": {
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "10px !important",
                px: 1.75,
                py: 0.75,
                fontWeight: 800,
                textTransform: "none",
                "&.Mui-selected": {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
                  borderColor: "primary.main",
                  color: "primary.main",
                },
              },
            }}
          >
            {oneClipOptions.map((sec) => (
              <ToggleButton key={sec} value={sec} aria-label={`${sec} seconds`}>
                {formatDurationLabel(sec)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      ) : null}

      {/* Fine-tune within a single clip when the model allows any length in range */}
      {!discrete && clipMax != null ? (
        <Box sx={{ mb: 1.5, px: 0.5 }}>
          <Slider
            size="small"
            min={min}
            max={clipMax}
            step={1}
            value={Math.min(clipMax, Math.max(min, willStitch ? clipMax : value))}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => formatDurationLabel(v)}
            onChange={(_, v) => applyDuration(v)}
            marks={[
              { value: min, label: formatDurationLabel(min) },
              { value: clipMax, label: formatDurationLabel(clipMax) },
            ]}
            aria-label={lang?.videoStudioDurationOneClip || "One clip"}
            sx={{
              "& .MuiSlider-markLabel": { fontSize: "0.65rem", top: 24 },
            }}
          />
        </Box>
      ) : null}

      {stitchable ? (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.75, lineHeight: 1.35 }}
          >
            {lang?.videoStudioDurationTotalLabel ||
              "Total length (up to 5 minutes — we split into scenes and join them)"}
          </Typography>

          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mb: 1.25 }}>
            {totalPresets.map((sec) => {
              const selected = value === sec;
              return (
                <Chip
                  key={sec}
                  clickable
                  size="small"
                  color={selected ? "primary" : "default"}
                  variant={selected ? "filled" : "outlined"}
                  label={formatDurationLabel(sec)}
                  onClick={() => applyDuration(sec)}
                  sx={{ fontWeight: 700 }}
                />
              );
            })}
          </Stack>

          <Box sx={{ px: 0.5 }}>
            <Slider
              size="small"
              min={min}
              max={totalMax}
              step={1}
              value={value}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => formatDurationLabel(v)}
              onChange={(_, v) => applyDuration(v)}
              marks={totalSliderMarks}
              aria-label={lang?.videoStudioDurationTotalLabel || "Total length"}
              sx={{
                "& .MuiSlider-markLabel": {
                  fontSize: "0.65rem",
                  top: 26,
                  color: "text.disabled",
                },
              }}
            />
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 1.25, lineHeight: 1.45 }}
          >
            {willStitch && clipMax
              ? (
                  lang?.videoStudioAutoStitchHint ||
                  "This model generates up to {clip}s per clip. Your {total} video becomes {count} scenes, then we join them."
                )
                  .replace("{clip}", String(clipMax))
                  .replace("{count}", String(sceneCount))
                  .replace("{total}", formatDurationLabel(value))
              : (
                  lang?.videoStudioDurationTotalHint ||
                  "Drag up to 5 minutes anytime. Lengths above {clip}s are split into scenes automatically."
                ).replace("{clip}", formatDurationLabel(clipMax || longerMin))}
          </Typography>
        </Box>
      ) : discrete ? (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.5, lineHeight: 1.4 }}
        >
          {(
            lang?.videoStudioDurationDiscreteHint ||
            "This model supports {options} per clip."
          ).replace(
            "{options}",
            discrete.map((s) => formatDurationLabel(s)).join(" / "),
          )}
        </Typography>
      ) : null}
    </Box>
  );
}
